import { type ReactElement, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { FieldValues, useForm } from 'react-hook-form';
import {
  Checkbox,
  StatusBar,
  Modal,
  PrimaryButton,
  RadioButton,
  SecondaryButton,
  SnackbarStatus,
  Select,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { DesignPlatformExportFlow, type ExportMenuPdfPayload } from '../DesignPlatformExportFlow';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useDesignPlatformAuth } from 'src/hooks/useDesignPlatformAuth';
import { useDesignPlatformTemplates } from 'src/hooks/useDesignPlatformTemplates';
import { getVenues } from 'src/api/getVenues/getVenues';
import { useVenueCategories } from 'src/hooks/useVenueCategories/useVenueCategories';
import { venueCategoriesToSelectOptions } from 'src/utils/venueCategories/mappedVenueCategories';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { initiateAuth, exportMenuPdf } from 'src/api/designPlatform';
import ModalImg from 'src/assets/images/chef-plating.png';
import { FileIcon } from 'src/assets/images/FileIcon';
import { CanvaLogo } from 'src/assets/images/CanvaLogo';
import { exportCSV } from 'src/api/exportCSV/exportCSV';
import { exportAllergensPDF } from 'src/api/exportAllergensPDF';
import { triggerPriceSchemeExport } from 'src/api/triggerPriceSchemeExport/triggerPriceSchemeExport';
import { error } from 'src/mocks/data';
import { IAsset } from 'src/helpers/menus/menus.types';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { getMenuDataToSave, ISavedMenuData } from './utilities/getMenuDataToSave';
import styles from './ExportModal.module.scss';
import { IAssetV2 } from 'src/api/getMenu/types';
import { IMealtime } from 'src/types/mealtimes.types';
import { ISection, IMenuItem } from 'src/types/menu.types';
import { getMenuDetailsV2 } from 'src/api/getMenuDetailsV2';

type AuthButtonState = 'loading' | 'pending' | 'unauthenticated' | 'authenticated';

const getAuthButtonState = (isLoading: boolean, isAuthenticated: boolean, status: string | null): AuthButtonState => {
  if (isLoading) {
    return 'loading';
  }
  if (status === 'pending_auth') {
    return 'pending';
  }
  if (isAuthenticated) {
    return 'authenticated';
  }
  return 'unauthenticated';
};

const AUTH_BUTTON_LABELS: Record<AuthButtonState, string> = {
  loading: 'Checking...',
  pending: 'Waiting for auth...',
  unauthenticated: 'Authenticate',
  authenticated: 'Authenticated',
};

interface ControlsProps {
  fileValue: string;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isSubmitting: boolean;
  selectedPriceSchemeId: string | undefined;
  allergensPDF: IAsset | IAssetV2 | null;
  isGeneratingPDF: boolean;
  closeModal: () => void;
  handlePDFFileDownload: () => void;
}

const renderDesignPlatformControls = (closeModal: () => void, isAuthenticated: boolean, isAuthLoading: boolean) => (
  <div className={styles['controls']}>
    <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={closeModal}>
      Cancel
    </SecondaryButton>
    <PrimaryButton size="full-width" disabled={!isAuthenticated || isAuthLoading} type="submit">
      Continue
    </PrimaryButton>
  </div>
);

const renderCsvControls = (
  closeModal: () => void,
  isSubmitting: boolean,
  fileValue: string,
  selectedPriceSchemeId: string | undefined,
) => (
  <div className={styles['controls']}>
    <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={closeModal}>
      Cancel
    </SecondaryButton>
    <PrimaryButton
      size="full-width"
      loading={isSubmitting}
      disabled={fileValue === ExportFileType.MENU_PRICES_CSV && !selectedPriceSchemeId}
      type="submit"
    >
      {isSubmitting ? 'Downloading...' : 'Download'}
    </PrimaryButton>
  </div>
);

const renderPdfControls = (
  allergensPDF: IAsset | IAssetV2 | null,
  isGeneratingPDF: boolean,
  isSubmitting: boolean,
  handlePDFFileDownload: () => void,
) => {
  const isLoading = isGeneratingPDF || isSubmitting;
  const buttonLabel = isLoading ? 'New file generating' : allergensPDF ? 'Regenerate file' : 'Generate';

  return (
    <div className={styles['controls']}>
      {allergensPDF ? (
        <SecondaryButton buttonStyle="outline" loading={isLoading} disabled={isLoading} size="full-width" type="submit">
          {buttonLabel}
        </SecondaryButton>
      ) : (
        <PrimaryButton loading={isLoading} disabled={isLoading} size="full-width" type="submit">
          {buttonLabel}
        </PrimaryButton>
      )}
      {allergensPDF && (
        <PrimaryButton size="full-width" onClick={handlePDFFileDownload} type="button">
          Download
        </PrimaryButton>
      )}
    </div>
  );
};

const ExportControls = ({
  fileValue,
  isAuthenticated,
  isAuthLoading,
  isSubmitting,
  selectedPriceSchemeId,
  allergensPDF,
  isGeneratingPDF,
  closeModal,
  handlePDFFileDownload,
}: ControlsProps) => {
  if (fileValue === ExportFileType.DESIGN_PLATFORM) {
    return renderDesignPlatformControls(closeModal, isAuthenticated, isAuthLoading);
  }

  if (fileValue === ExportFileType.COMTREX_CSV || fileValue === ExportFileType.MENU_PRICES_CSV) {
    return renderCsvControls(closeModal, isSubmitting, fileValue, selectedPriceSchemeId);
  }

  return renderPdfControls(allergensPDF, isGeneratingPDF, isSubmitting, handlePDFFileDownload);
};

export enum ExportFileType {
  DESIGN_PLATFORM = 'design-platform',
  COMTREX_CSV = 'comtrex-csv',
  MENU_PRICES_CSV = 'menu-prices-csv',
  ALLERGEN_PDF = 'allergen-pdf',
}

export interface IExportModal {
  title: string;
  closeModal: () => void;
  open: boolean;
  id: string;
  name: string;
  allergensPDF: IAsset | IAssetV2 | null;
  priceSchemes?: { name: string; value: string }[];
  mealtimes?: IMealtime[];
  sections?: ISection[];
  items?: IMenuItem[];
}

const DESIGN_PLATFORM_FEATURE_FLAG = 'export-menu-to-design-platform';

const ExportModal = ({
  title,
  closeModal,
  open,
  id,
  name,
  allergensPDF,
  priceSchemes = [],
  mealtimes = [],
  sections = [],
  items = [],
}: IExportModal): ReactElement => {
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      file: ExportFileType.COMTREX_CSV,
      showPotentialAllergens: true,
    },
  });
  const { featureFlags } = useAuthWrapper();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { showSnackbar } = useSnackbar();
  const fileValue = watch('file');
  const [selectedPriceSchemeId, setSelectedPriceSchemeId] = useState<string | undefined>();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showCanvaFlow, setShowCanvaFlow] = useState(false);

  const hasDesignPlatformFeatureFlag = featureFlags?.includes(DESIGN_PLATFORM_FEATURE_FLAG) ?? false;
  const isDesignPlatformSelected = fileValue === ExportFileType.DESIGN_PLATFORM;

  const {
    addonId,
    platformName,
    isAuthenticated,
    status: authStatus,
    isLoading: isAuthLoading,
    hasDesignPlatform,
  } = useDesignPlatformAuth({
    // Only poll when modal is open AND design platform option is selected
    pollEnabled: open && isDesignPlatformSelected,
  });

  // Fetch templates when authenticated
  const { data: templates = [], isLoading: templatesLoading } = useDesignPlatformTemplates(addonId, {
    enabled: isAuthenticated && showCanvaFlow,
  });

  // Fetch venues only when Canva flow is shown
  const { data: venues = [], isLoading: venuesLoading } = useQuery({
    queryKey: ['venues'],
    queryFn: getVenues,
    enabled: showCanvaFlow,
  });
  const { nestedCategories: venueCategories } = useVenueCategories();

  // Convert venue categories to MultiSelect format
  const venueCategoryOptions = useMemo(() => venueCategoriesToSelectOptions(venueCategories), [venueCategories]);

  // Fetch price schemes for the Canva flow
  const { data: priceSchemesList = [], isLoading: priceSchemesLoading } = usePriceSchemes();

  // Fetch menu details (sections, items, modifier groups, mealtimes) for truncation warnings
  const { data: menuDetailsV2 } = useQuery({
    queryKey: ['menu-details-v2', id],
    queryFn: () => getMenuDetailsV2({ menuId: id, includes: ['sections', 'items', 'modifier_groups', 'mealtimes'] }),
    enabled: showCanvaFlow,
  });

  const showDesignPlatformOption = hasDesignPlatformFeatureFlag && hasDesignPlatform;
  const isCanva = platformName?.toLowerCase() === 'canva';
  const platformLogo = isCanva ? <CanvaLogo /> : undefined;
  const isAuthInProgress = isAuthLoading || isAuthenticating || authStatus === 'pending_auth';
  const isAuthButtonDisabled = isAuthenticated || isAuthInProgress;

  const handleAuthenticate = async () => {
    if (!addonId) {
      return;
    }

    setIsAuthenticating(true);
    try {
      const response = await initiateAuth(addonId);
      const { authUrl, authenticated } = response.data;
      if (authUrl) {
        window.open(authUrl, '_blank', 'noopener,noreferrer');
      } else if (authenticated) {
        showNotification({
          status: SnackbarStatus.INFO,
          message: `Already authenticated with ${platformName || 'design platform'}`,
        });
      } else {
        showNotification({
          status: SnackbarStatus.ERROR,
          message: 'Unable to start authentication. Please try again.',
        });
      }
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: `Failed to initiate ${platformName || 'design platform'} authentication`,
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const date = allergensPDF ? formatDate(new Date(allergensPDF.create_date)) : '';
  const time = allergensPDF ? formatTime(new Date(allergensPDF.create_date)) : '';

  const savedMenusData: ISavedMenuData[] | null = JSON.parse(sessionStorage.getItem('pdfGeneration'));
  const savedMenuData: ISavedMenuData = savedMenusData?.find((data: ISavedMenuData) => data?.menuId === id);
  const assetTime = allergensPDF ? new Date(allergensPDF.create_date) : {};
  const isGeneratingPDF = savedMenuData && (!allergensPDF || assetTime < new Date(savedMenuData.startTime));

  const onSubmit = async (data: FieldValues) => {
    clearError();
    // Handle design platform by opening the Canva flow instead of submitting
    if (data.file === ExportFileType.DESIGN_PLATFORM) {
      setShowCanvaFlow(true);
      return;
    }
    if (data.file === ExportFileType.COMTREX_CSV) {
      try {
        const response = await exportCSV({
          payload: { destinations: ['COMTREX'] },
          menuId: id,
        });

        saveAs(response, `${name}.csv`);
        showNotification({
          message: `You have successfully exported menu ${name}.`,
          status: SnackbarStatus.SUCCESS,
        });
        closeModal();
      } catch {
        showNotification({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
      }
    } else if (data.file === ExportFileType.MENU_PRICES_CSV) {
      try {
        await triggerPriceSchemeExport(selectedPriceSchemeId, id);
        closeModal();
      } catch {
        showNotification(error);
      }
    } else if (data.file === ExportFileType.ALLERGEN_PDF) {
      try {
        await exportAllergensPDF(id, data.showPotentialAllergens);

        const dataToSave = getMenuDataToSave(id, savedMenusData);
        sessionStorage.setItem('pdfGeneration', JSON.stringify(dataToSave));

        showNotification({
          message: 'New Allergen PDF is being generated',
          status: SnackbarStatus.INFO,
        });
      } catch {
        showNotification({
          message: "New file can't be generated, please try again later",
          status: SnackbarStatus.ERROR,
        });
      }
    }
  };

  const handlePDFFileDownload = async () => {
    const pdfUrl = 'asset_url' in allergensPDF ? allergensPDF.asset_url : allergensPDF?.asset_variants[0]?.url;

    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      saveAs(blob, `${name}-${date}-${time}-allergen.pdf`);
    } catch {
      showNotification({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
    }
  };

  const handleCanvaExportSubmit = async (payload: ExportMenuPdfPayload) => {
    try {
      await exportMenuPdf(id, payload);
      setShowCanvaFlow(false);
      closeModal();
      showSnackbar({
        status: SnackbarStatus.INFO,
        message: 'Your download is in progress',
      });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Failed to export menu. Please try again.',
      });
    }
  };

  const handleCloseCanvaFlow = () => {
    setShowCanvaFlow(false);
  };

  // Transform venues to the format expected by DesignPlatformExportModal
  const transformedVenues = venues.map((venue) => ({
    venueId: venue.venue_id,
    name: venue.name,
    isTrial: venue.is_trial,
  }));

  // Transform menu data from V2 endpoint for DesignPlatformExportModal
  const menuForTruncation = useMemo(() => {
    if (!menuDetailsV2) {
      // Fall back to props data if V2 not loaded yet
      return {
        sections: sections.map((section) => ({
          sectionId: section.sectionId,
          name: section.name,
          description: section.description,
          displayOrder: section.displayOrder,
          items: (section.items || []).map((item) => ({
            itemId: item.itemId,
            name: item.name,
            consumerName: item.consumerName,
            displayOrder: item.displayOrder,
          })),
          parentSectionId: section.parentSectionId,
        })),
        items: items.map((item) => ({
          itemId: item.itemId,
          name: item.name,
          consumerName: item.consumerName,
          displayOrder: item.displayOrder,
        })),
        modifierGroups: [],
        mealtimes: [],
      };
    }

    // Build item lookup map from V2 data
    const itemsById = new Map(
      (menuDetailsV2.items || []).map((item, index) => [
        item.id,
        {
          itemId: item.id,
          name: item.name,
          consumerName: item.consumer_name,
          displayOrder: index,
          modifierGroupIds: item.modifier_group_ids,
        },
      ]),
    );

    // Transform sections with embedded items
    const transformedSections = (menuDetailsV2.sections || []).map((section) => {
      // Find parent section to determine if this is a subsection
      const parentSection = (menuDetailsV2.sections || []).find((s) => s.sub_sections.includes(section.id));

      return {
        sectionId: section.id,
        name: section.name,
        description: section.description || '',
        displayOrder: section.display_order,
        items: section.items
          .map((itemId, itemIndex) => {
            const item = itemsById.get(itemId);
            return item ? { ...item, displayOrder: itemIndex } : undefined;
          })
          .filter((item): item is NonNullable<typeof item> => item !== undefined),
        parentSectionId: parentSection?.id || null,
      };
    });

    // Transform items (flat list with modifierGroupIds)
    const transformedItems = (menuDetailsV2.items || []).map((item, index) => ({
      itemId: item.id,
      name: item.name,
      consumerName: item.consumer_name,
      displayOrder: index,
      modifierGroupIds: item.modifier_group_ids,
    }));

    // Transform modifier groups
    const transformedModifierGroups = (menuDetailsV2.modifier_groups || []).map((mg) => ({
      id: mg.id,
      name: mg.name,
      modifiers: mg.modifiers.map((mod) => ({
        id: mod.id,
        itemId: mod.item_id,
      })),
      external_systems: mg.external_systems,
    }));

    // Transform mealtimes (pass through with section IDs)
    const transformedMealtimes = (menuDetailsV2.mealtimes || []).map((mt) => ({
      id: mt.id,
      name: mt.name,
      sections: mt.sections ?? null,
      schedules: mt.schedules,
    }));

    return {
      sections: transformedSections,
      items: transformedItems,
      modifierGroups: transformedModifierGroups,
      mealtimes: transformedMealtimes,
    };
  }, [menuDetailsV2, sections, items]);

  // Render the design platform flow modal
  if (showCanvaFlow) {
    const flowContent = (
      <DesignPlatformExportFlow
        menuName={name}
        templates={templates}
        templatesLoading={templatesLoading}
        mealtimes={mealtimes}
        venues={transformedVenues}
        venuesLoading={venuesLoading}
        venueCategories={venueCategories}
        venueCategoryOptions={venueCategoryOptions}
        priceSchemes={priceSchemesList}
        priceSchemesLoading={priceSchemesLoading}
        platformName={platformName || 'Design Platform'}
        platformLogo={platformLogo}
        menu={menuForTruncation}
        onSubmit={handleCanvaExportSubmit}
        onCancel={handleCloseCanvaFlow}
      />
    );

    return (
      <Modal closeModal={handleCloseCanvaFlow} open={open} contentPosition="top">
        {flowContent}
      </Modal>
    );
  }

  // Render the export form modal
  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['export-form']}>
      <Typography size="xxlarge" weight="semi-bold">
        {title}
      </Typography>
      <div className={styles['options-container']}>
        {showDesignPlatformOption && (
          <div className={styles['design-platform-option']}>
            <div className={styles['design-platform-radio-row']}>
              <RadioButton name="file" value={ExportFileType.DESIGN_PLATFORM} register={register}>
                <span className={styles['design-platform-label']}>
                  <Typography>Menu using {platformName}</Typography>
                  {isCanva && <CanvaLogo />}
                </span>
                {!isAuthenticated && !isAuthLoading && (
                  <Typography size="small" className={styles['design-platform-subtitle']}>
                    Authentication Required
                  </Typography>
                )}
              </RadioButton>
              <button
                type="button"
                className={styles['design-platform-connect']}
                onClick={!isAuthButtonDisabled ? handleAuthenticate : undefined}
                disabled={isAuthButtonDisabled}
              >
                <Typography size="small">
                  {AUTH_BUTTON_LABELS[getAuthButtonState(isAuthLoading, isAuthenticated, authStatus)]}
                </Typography>
              </button>
            </div>
          </div>
        )}

        <RadioButton name="file" value={ExportFileType.COMTREX_CSV} register={register}>
          Menu Details (CSV)
        </RadioButton>

        <div className={styles['menu-prices-radio']}>
          <RadioButton name="file" value={ExportFileType.MENU_PRICES_CSV} register={register}>
            <Typography>Menu prices (CSV)*</Typography>
            <Typography size="small" className={styles['design-platform-subtitle']}>
              *This export excludes bundle &amp; modifier override information
            </Typography>
          </RadioButton>
        </div>
        <div className={styles['price-scheme-select']}>
          <Select
            label="Choose a price scheme"
            options={priceSchemes}
            onChange={(selected) => setSelectedPriceSchemeId(selected)}
            value={selectedPriceSchemeId}
            disabled={fileValue !== 'menu-prices-csv'}
          />
        </div>

        <div>
          <RadioButton name="file" value={ExportFileType.ALLERGEN_PDF} register={register}>
            Allergen information (PDF)
          </RadioButton>
          {fileValue === 'allergen-pdf' && (
            <div className={styles['pdf-message-box']}>
              {!isGeneratingPDF && !allergensPDF && <Typography>Generate file below to download export</Typography>}
              {isGeneratingPDF && !allergensPDF && (
                <Typography>This might take a few mins, please come back later and refresh the page.</Typography>
              )}
              {allergensPDF && (
                <div className={styles['file']}>
                  <FileIcon className={styles['file-icon']} />
                  <Typography className={styles['file-text']}>{`File generated on ${date} at ${time}`}</Typography>
                </div>
              )}

              <div className={styles['show-may-contain-checkbox']}>
                <Checkbox
                  name="showPotentialAllergens"
                  register={register}
                  label="Include 'May Contain' information"
                  disabled={isSubmitting || isGeneratingPDF}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <ExportControls
        fileValue={fileValue}
        isAuthenticated={isAuthenticated}
        isAuthLoading={isAuthLoading}
        isSubmitting={isSubmitting}
        selectedPriceSchemeId={selectedPriceSchemeId}
        allergensPDF={allergensPDF}
        isGeneratingPDF={isGeneratingPDF}
        closeModal={closeModal}
        handlePDFFileDownload={handlePDFFileDownload}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );

  return (
    <Modal closeModal={closeModal} open={open} image={ModalImg} imageAlt="Picture of a chef plating up">
      {formContent}
    </Modal>
  );
};

export default ExportModal;
